import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import simpleheat from "simpleheat";

export type SimpleHeatPoint = {
    lat: number;
    lng: number;
    intensity?: number;
};

type SimpleHeatLayerProps = {
    points: SimpleHeatPoint[];
    radius?: number;
    blur?: number;
    max?: number;
    minOpacity?: number;
    opacity?: number;
    gradient?: Record<number, string>;
};

const DEFAULT_GRADIENT: Record<number, string> = {
    0.2: "#2ecc71",
    0.45: "#f1c40f",
    0.7: "#e67e22",
    1.0: "#e74c3c",
};

/**
 * Canvas heatmap overlay powered by `simpleheat`, for use inside a react-leaflet MapContainer.
 */
export default function SimpleHeatLayer({
    points,
    radius = 25,
    blur = 15,
    max = 3,
    minOpacity = 0.15,
    opacity = 0.85,
    gradient = DEFAULT_GRADIENT,
}: SimpleHeatLayerProps) {
    const map = useMap();

    useEffect(() => {
        const canvas = L.DomUtil.create("canvas", "leaflet-zoom-hide") as HTMLCanvasElement;
        const pane = map.getPanes().overlayPane;
        pane.appendChild(canvas);

        const heat = simpleheat(canvas);
        heat.radius(radius, blur);
        heat.gradient(gradient);
        heat.max(max);

        const redraw = () => {
            const size = map.getSize();
            canvas.width = size.x;
            canvas.height = size.y;
            canvas.style.width = `${size.x}px`;
            canvas.style.height = `${size.y}px`;
            canvas.style.opacity = String(opacity);
            L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
            heat.resize();

            const data: Array<[number, number, number]> = [];
            points.forEach((point) => {
                const containerPoint = map.latLngToContainerPoint([point.lat, point.lng]);
                if (
                    containerPoint.x < -radius ||
                    containerPoint.y < -radius ||
                    containerPoint.x > size.x + radius ||
                    containerPoint.y > size.y + radius
                ) {
                    return;
                }
                data.push([containerPoint.x, containerPoint.y, point.intensity ?? 1]);
            });

            const localMax = data.reduce((acc, [, , intensity]) => Math.max(acc, intensity), 0);
            heat.clear();
            heat.max(Math.max(max, localMax));
            heat.data(data);
            try {
                heat.draw(minOpacity);
            } catch {
                // Ignore zero-size canvas draw errors during layout.
            }
        };

        redraw();
        map.on("moveend viewreset zoomanim resize", redraw);

        return () => {
            map.off("moveend viewreset zoomanim resize", redraw);
            if (pane.contains(canvas)) {
                pane.removeChild(canvas);
            }
        };
    }, [map, points, radius, blur, max, minOpacity, opacity, gradient]);

    return null;
}
